import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import {
  checkCanEditReel,
  checkCanEditSessionChapters,
  checkCanViewTrainingArchive,
  checkIsAdminOrHigher,
  deleteReelPermanently,
  deleteSessionPermanently,
  fetchArchivedReels,
  fetchArchivedSessions,
  restoreReel,
  restoreSession,
} from '../../services/training';

function ArchivedSessionRow({ session, isAdmin, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!session.uploadedBy) return;
    checkCanEditSessionChapters(session.uploadedBy).then(setCanEdit).catch(() => setCanEdit(false));
  }, [session.uploadedBy]);

  const run = async (action, fn) => {
    setError('');
    setBusy(action);
    try {
      await fn();
      onChanged?.();
      setConfirmDelete(false);
    } catch (err) {
      setError(err.message || 'No se pudo completar la acción');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="p-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] flex flex-col md:flex-row md:items-center gap-4">
      <button
        type="button"
        onClick={() => navigate(`/app/entrenamientos/${session.id}`)}
        className="flex-1 text-left min-w-0"
      >
        <p className="font-black text-[var(--text-main)] uppercase tracking-tight truncate">{session.title}</p>
        <p className="text-xs text-[var(--text-secondary)] mt-1">{session.speaker} · {session.duration}</p>
      </button>
      {error && <p className="text-xs text-red-300">{error}</p>}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => run('restore', () => restoreSession(session.id))}
            className="px-4 py-2 rounded-full border border-brand-green/40 text-brand-green text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
          >
            Restaurar
          </button>
          {isAdmin && (
            confirmDelete ? (
              <>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => run('delete', () => deleteSessionPermanently(session.id))}
                  className="px-4 py-2 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-widest"
                >
                  Confirmar
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="text-[10px] text-[var(--text-secondary)]">
                  Cancelar
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => setConfirmDelete(true)}
                className="px-4 py-2 rounded-full border border-red-400/40 text-red-300 text-[10px] font-black uppercase tracking-widest"
              >
                Eliminar
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

function ArchivedReelRow({ reel, isAdmin, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!reel.uploadedBy) return;
    checkCanEditReel(reel.uploadedBy).then(setCanEdit).catch(() => setCanEdit(false));
  }, [reel.uploadedBy]);

  const run = async (action, fn) => {
    setError('');
    setBusy(action);
    try {
      await fn();
      onChanged?.();
      setConfirmDelete(false);
    } catch (err) {
      setError(err.message || 'No se pudo completar la acción');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="p-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] flex flex-col md:flex-row md:items-center gap-4">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <img src={reel.videoThumbnail} alt="" className="w-14 h-20 object-cover rounded-lg flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-black text-[var(--text-main)] uppercase tracking-tight truncate">{reel.title}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">{reel.speaker}</p>
        </div>
      </div>
      {error && <p className="text-xs text-red-300">{error}</p>}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => navigate(`/app/reels/${reel.id}/editar`)}
            className="px-4 py-2 rounded-full border border-[var(--border-subtle)] text-[10px] font-black uppercase tracking-widest"
          >
            Editar
          </button>
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => run('restore', () => restoreReel(reel.id))}
            className="px-4 py-2 rounded-full border border-brand-green/40 text-brand-green text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
          >
            Restaurar
          </button>
          {isAdmin && (
            confirmDelete ? (
              <>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => run('delete', () => deleteReelPermanently(reel.id))}
                  className="px-4 py-2 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-widest"
                >
                  Confirmar
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="text-[10px] text-[var(--text-secondary)]">
                  Cancelar
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => setConfirmDelete(true)}
                className="px-4 py-2 rounded-full border border-red-400/40 text-red-300 text-[10px] font-black uppercase tracking-widest"
              >
                Eliminar
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default function TrainingArchivePage() {
  const { loading: authLoading } = useAuth();
  const [allowed, setAllowed] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tab, setTab] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sessionRows, reelRows] = await Promise.all([
        fetchArchivedSessions(),
        fetchArchivedReels(),
      ]);
      setSessions(sessionRows);
      setReels(reelRows);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([checkCanViewTrainingArchive(), checkIsAdminOrHigher()])
      .then(([canView, admin]) => {
        if (!active) return;
        setAllowed(canView);
        setIsAdmin(admin);
        if (canView) load();
        else setLoading(false);
      })
      .catch(() => {
        if (active) {
          setAllowed(false);
          setLoading(false);
        }
      });
    return () => { active = false; };
  }, [load]);

  if (authLoading || allowed === null) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/app/entrenamientos" replace />;
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/app/entrenamientos"
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)]"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Archivo
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Contenido archivado, oculto para los miembros. Restaura o elimina permanentemente (solo admin).
          </p>
        </div>
      </div>

      <div className="flex gap-6 border-b border-[var(--border-subtle)] mb-8">
        {[
          { id: 'sessions', label: 'Sesiones', count: sessions.length },
          { id: 'reels', label: 'Reels', count: reels.length },
        ].map(({ id, label, count }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`pb-4 text-[11px] font-black uppercase tracking-[0.15em] ${
              tab === id ? 'text-[var(--text-main)] border-b-2 border-brand-cyan' : 'text-[var(--text-secondary)]'
            }`}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-secondary)] font-bold uppercase tracking-widest">Cargando archivo…</p>
      ) : tab === 'sessions' ? (
        sessions.length === 0 ? (
          <p className="text-[var(--text-secondary)]">No hay sesiones archivadas.</p>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <ArchivedSessionRow
                key={session.id}
                session={session}
                isAdmin={isAdmin}
                onChanged={load}
              />
            ))}
          </div>
        )
      ) : reels.length === 0 ? (
        <p className="text-[var(--text-secondary)]">No hay reels archivados.</p>
      ) : (
        <div className="space-y-3">
          {reels.map((reel) => (
            <ArchivedReelRow
              key={reel.id}
              reel={reel}
              isAdmin={isAdmin}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}
